import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
// import SvgArrowDropdown from "../images/ArrowDropDown";

const Pagination = ({
  recordsPerPage,
  setRecordsPerPage,
  currentPage,
  setCurrentPage,
  totalRecords,
  className = "",
  recordsPerPageOptions = [10, 25, 50],
}) => {
  const maxOption = Math.max(...recordsPerPageOptions);
  const isAllSelected = recordsPerPage > maxOption;
  const effectiveRecordsPerPage = isAllSelected ? totalRecords : recordsPerPage;
  const totalPages =
    totalRecords > 0 ? Math.ceil(totalRecords / effectiveRecordsPerPage) : 0;

  const startRecord =
    totalRecords > 0 ? (currentPage - 1) * effectiveRecordsPerPage + 1 : 0;

  const endRecord = isAllSelected
    ? totalRecords
    : Math.min(currentPage * effectiveRecordsPerPage, totalRecords);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const getPageNumbers = () => {
    const pageNumbers = [];

    if (totalPages < 1) return pageNumbers;

    pageNumbers.push(1);

    if (currentPage > 3) {
      pageNumbers.push("...");
    }

    const start = Math.max(2, currentPage - 2);
    const end = Math.min(totalPages - 1, currentPage + 2);

    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }

    if (currentPage < totalPages - 2) {
      pageNumbers.push("...");
    }

    if (totalPages > 1 && pageNumbers[pageNumbers.length - 1] !== totalPages) {
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div
      className={`px-6 py-4 border-t border-[var(--color-border-gray)] bg-theme-white flex items-center justify-between ${className}`}
    >
      <div className="flex items-center gap-4">
        {/* CUSTOM DROPDOWN START */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center justify-between w-20 border border-[#007ab7] rounded-lg px-3 py-1.5 text-sm font-medium text-[var(--color-dark)] bg-white hover:bg-gray-50 transition-all"
          >
            {isAllSelected ? "All" : recordsPerPage}
            <ChevronDown
              className={`w-4 h-4 ml-2 transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </button>

          {isOpen && (
            <div className="absolute bottom-full mb-2 w-full bg-white border border-[#E0F2FE] rounded-lg shadow-xl overflow-hidden z-50">
              {recordsPerPageOptions.map((option) => (
                <div
                  key={option}
                  onClick={() => {
                    setRecordsPerPage(option);
                    setCurrentPage(1);
                    setIsOpen(false);
                  }}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-[#007ab7] hover:text-white transition-colors"
                >
                  {option}
                </div>
              ))}
              <div
                onClick={() => {
                  setRecordsPerPage(10000);
                  setCurrentPage(1);
                  setIsOpen(false);
                }}
                className="px-3 py-2 text-sm cursor-pointer border-t border-gray-100 hover:bg-[#007AB7] hover:text-white transition-colors"
              >
                All
              </div>
            </div>
          )}
        </div>
        {/* CUSTOM DROPDOWN END */}

        <span className="text-sm text-[var(--color-dark)]">
          Records Per Page
        </span>
      </div>

      <div className="text-sm text-[var(--color-dark)]">
        Showing {startRecord} - {endRecord} of {totalRecords}
      </div>

      <div className="flex items-center">
        <button
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1 || totalPages === 0}
          className="p-1 border-y border-l border-[#007AB7] rounded-l-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Previous page"
        >
          {/* <SvgArrowDropDown className="w-6 h-6 rotate-180 text-[var(--color-font-blue2)]" /> */}
          <ArrowLeft className="w-6 h-6 text-[#007AB7] " />
          {/* <p>fr</p> */}
        </button>

        {pageNumbers.map((page, index) =>
          typeof page === "string" ? (
            <span
              key={`ellipsis-${index}`}
              className="px-3 py-1 border-[#007AB7] border-y border-l  text-[var(--color-font-blue2)] flex items-center"
            >
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1 border-y border-l last:border-r border-[#007AB7] ${
                currentPage === page
                  ? "bg-[#007AB7]  text-white py-0.5 border-[#007AB7] border-y"
                  : "text-[#007AB7]  hover:bg-gray-50"
              }`}
              aria-label={`Page ${page}`}
              aria-current={currentPage === page ? "page" : undefined}
            >
              {page}
            </button>
          ),
        )}

        <button
          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages || totalPages === 0}
          className="p-1 border-y border-r border-[#007AB7] rounded-r-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Next page"
        >
          <ArrowRight className="w-6 h-6 text-[#007AB7]" />
          {/* <SvgArrowDropdown className="w-6 h-6 text-[var(--color-font-blue2)]" /> */}
          {/* <p>pr</p> */}
        </button>
      </div>
    </div>
  );
};

export default Pagination;
